/**
 * Mobile App Build Branding Config
 *
 * Build-time branding (colors + display text) that the mobile app reads from
 * its per-school `build.config.json`. SCH-124 moves the source of truth for
 * these values into the MobileApp registry row; the EAS staging script
 * (`apps/mobile/scripts/stage-school-assets.sh`) fetches them from the public
 * asset-manifest-by-school-app endpoint and writes a `build.config.json` into
 * the staged directory the mobile import hook already reads.
 *
 * Field provenance (build.config.json field -> registry source):
 *   APP_NAME                -> MobileApp.name
 *   EXPO_SLUG               -> MobileApp.expoSlug
 *   EXPO_PROJECT_ID         -> MobileApp.expoProjectId
 *   BUNDLE_ID               -> MobileApp.bundleId
 *   SPLASH_BG_COLOR         -> MobileApp.splashBackgroundColor
 *   ADAPTIVE_ICON_BG_COLOR  -> MobileApp.adaptiveIconBackgroundColor
 *   LOGIN_BG_COLOR_*        -> MobileApp.brandingConfig.loginBackground.{light,dark}
 *   ONBOARDING_BG_COLOR_*   -> MobileApp.brandingConfig.onboardingBackground.{light,dark}
 *   BUTTON_COLOR            -> MobileApp.brandingConfig.button.color
 *   BUTTON_TEXT_COLOR       -> MobileApp.brandingConfig.button.textColor
 *   PRE_AUTH_THEME          -> MobileApp.brandingConfig.preAuthTheme
 *   LOGIN_TEXT              -> MobileApp.brandingConfig.loginText (legacy; content lives in onboarding-config.ts)
 *
 * The remainder that has no first-class MobileApp column (the color set + the
 * pre-auth HSL theme) is stored in the single JSON column
 * `MobileApp.brandingConfig`, validated against `MobileAppBrandingConfigSchema`.
 */

import { z } from 'zod'

// HSL triplet, e.g. "147 32% 24%" — mirrors apps/mobile pre-auth-theme.schema.
const HSLColorSchema = z
  .string()
  .regex(/^\d{1,3}(\.\d+)?\s+\d{1,3}(\.\d+)?%\s+\d{1,3}(\.\d+)?%$/, 'Must be a valid HSL color (e.g. "240 10% 3.9%")')

// Hex color, e.g. "#2C5234" or "#FFF".
const HexColorSchema = z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Must be a hex color (e.g. #2C5234)')

export const MobileAppPreAuthThemeModeSchema = z.object({
  background: HSLColorSchema,
  foreground: HSLColorSchema,
  primary: HSLColorSchema,
  primaryForeground: HSLColorSchema,
  muted: HSLColorSchema,
  mutedForeground: HSLColorSchema
})
export type MobileAppPreAuthThemeMode = z.infer<typeof MobileAppPreAuthThemeModeSchema>

export const MobileAppPreAuthThemeSchema = z.object({
  light: MobileAppPreAuthThemeModeSchema,
  dark: MobileAppPreAuthThemeModeSchema
})
export type MobileAppPreAuthTheme = z.infer<typeof MobileAppPreAuthThemeSchema>

/**
 * What a device uses when the registry holds no `preAuthTheme` for its app.
 *
 * NOT the source of truth. The app's own `DEFAULT_PRE_AUTH_THEME`
 * (`apps/mobile/src/schemas/pre-auth-theme.schema.ts`) is what actually renders, and it
 * is baked into the bundle, so nothing here can change what a phone shows. This copy
 * exists so the super-admin editor can say which slots a school has actually overridden
 * instead of presenting twelve boilerplate triplets as if each were a decision.
 *
 * KEEP THE TWO IN STEP. `apps/mobile` cannot import from a package it does not depend on
 * at build time, so this is a deliberate duplicate; if the mobile default changes, change
 * it here too, or the editor will mark unchanged slots as overridden.
 */
export const DEFAULT_PRE_AUTH_THEME: MobileAppPreAuthTheme = {
  light: {
    background: '0 0% 100%',
    foreground: '240 10% 3.9%',
    primary: '240 5.9% 10%',
    primaryForeground: '0 0% 98%',
    muted: '240 4.8% 95.9%',
    mutedForeground: '240 3.8% 46.1%'
  },
  dark: {
    background: '240 10% 3.9%',
    foreground: '0 0% 98%',
    primary: '0 0% 98%',
    primaryForeground: '240 5.9% 10%',
    muted: '240 3.7% 15.9%',
    mutedForeground: '240 5% 64.9%'
  }
}

const LightDarkColorSchema = z.object({
  light: HexColorSchema,
  dark: HexColorSchema
})

/**
 * The login layout family (`apps/mobile/src/components/login/`): `classic` is the
 * default every school gets, `photo` the full-bleed photographic variant, `sheet`
 * the bottom-sheet one.
 *
 * Which one a build uses is decided at BUILD time by the metro rule keyed on
 * `SCHOOL_APP` (`apps/mobile/scripts/metro/resolve-active-login-screen.js`), which
 * reads THIS value out of the bundle's `login-layout.json` — the snapshot's copy of
 * it. Nothing at runtime branches on it, which is deliberate (plan 2026-07-22-005 R3).
 * Until SCH-1904 a committed `src/components/login/schools/<school>/login-screen.tsx`
 * re-export beat the bundle for five schools, so this field was unenforceable for
 * them; that tree is gone and this value is now the only input.
 *
 * The registry value is therefore the DECISION OF RECORD, not a live switch: it is
 * what the app is supposed to be on, editable and auditable without reading the
 * mobile repo. Applying it is still the repo one-liner + a build. Do not describe
 * it to operators as taking effect on its own.
 */
export const MobileAppLoginLayoutSchema = z.enum(['classic', 'photo', 'sheet'])
export type MobileAppLoginLayout = z.infer<typeof MobileAppLoginLayoutSchema>

/**
 * The branding remainder stored in `MobileApp.brandingConfig` (JSON column).
 * All fields optional: a school may store only what differs from the platform
 * defaults, and a row with no branding column at all is valid (defaults apply).
 */
export const MobileAppBrandingConfigSchema = z
  .object({
    loginBackground: LightDarkColorSchema.optional(),
    onboardingBackground: LightDarkColorSchema.optional(),
    button: z
      .object({
        color: HexColorSchema,
        textColor: HexColorSchema
      })
      .optional(),
    loginText: z.string().optional(),
    preAuthTheme: MobileAppPreAuthThemeSchema.optional(),

    // ── Pre-login shape decisions (registry-declared) ────────────────────────────
    // Which login layout the build uses, and whether it shows the onboarding
    // carousel. They live HERE rather than on `onboardingConfig` because that
    // object is pre-login *copy*, replaced whole by the client when the registry
    // serves it — writing a half-filled copy config to flip a switch would blank a
    // school's baked wording. These two are shape, not copy.
    //
    // `onboardingEnabled` here is the DECISION OF RECORD. The public
    // onboarding-config endpoint derives its top-level `onboardingEnabled` from
    // this field first, then the stored copy's mirror, else `null`; the device
    // resolves that against its baked snapshot. Full precedence is documented
    // once, on `getOnboardingEnabled()` in `apps/mobile/src/config/build-config.ts`.
    // Do not restate it here. (The bundled one-way `false` that used to outrank
    // both was deleted by plan 2026-09-01-2310 U3 / KTD5, which is what makes
    // this field the switch a super-admin can actually move.)
    loginLayout: MobileAppLoginLayoutSchema.optional(),
    onboardingEnabled: z.boolean().optional()
  })
  .strict()
export type MobileAppBrandingConfig = z.infer<typeof MobileAppBrandingConfigSchema>

/**
 * The build-time branding payload the staging script materializes into
 * `build.config.json`. Combines the first-class MobileApp columns
 * (app_name / slug / colors) with the JSON-column remainder. Every field is
 * optional so a partially-configured registry row still produces a usable
 * (default-padded) build config rather than failing the build.
 */
export const MobileAppBuildBrandingSchema = z.object({
  appName: z.string().optional(),
  bundleId: z.string().optional(),
  expoSlug: z.string().optional(),
  expoProjectId: z.string().optional(),
  splashBackgroundColor: z.string().optional(),
  // Android adaptive-icon background layer. Absent ⇒ render falls back to
  // `splashBackgroundColor` (see render-build-config.ts).
  adaptiveIconBackgroundColor: z.string().optional(),
  loginBackground: LightDarkColorSchema.optional(),
  onboardingBackground: LightDarkColorSchema.optional(),
  button: z
    .object({
      color: HexColorSchema,
      textColor: HexColorSchema
    })
    .optional(),
  loginText: z.string().optional(),
  preAuthTheme: MobileAppPreAuthThemeSchema.optional(),

  // ── Pre-login shape decisions, carried through from `brandingConfig` ─────────
  // The snapshot step (plan 2026-09-01-2310 KTD2) reads this manifest and nothing
  // else, so the two shape decisions have to travel on it: `loginLayout` picks the
  // layout file at bundle time, `onboardingEnabled` is the carousel decision of
  // record the device falls back through. `assembleBuildBranding` already spread
  // them out of the parsed `brandingConfig`; until this declaration existed the
  // schema silently dropped them on the way out.
  loginLayout: MobileAppLoginLayoutSchema.optional(),
  onboardingEnabled: z.boolean().optional()
})
export type MobileAppBuildBranding = z.infer<typeof MobileAppBuildBrandingSchema>
